import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Clock,
  Trash2,
  Edit,
  Plus,
  Loader2,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Home,
  Video,
  Building
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { format } from "date-fns";
import { formatTime12Hour, formatDateMST, formatShortDateMST } from "@/utils/timeUtils";
import { 
  MEETING_TYPES, 
  getMeetingTypeLabel, 
  getMeetingTypeColor, 
  DEFAULT_MEETING_TYPE,
  MEETING_FORMATS,
  getMeetingFormatLabel,
  getMeetingFormatColor
} from "@/constants/meetingTypes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAppointments = () => {
  const { token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [unavailability, setUnavailability] = useState({ weekly: [], specific_dates: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create Appointment State
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedDuration, setSelectedDuration] = useState(30);
  const [selectedMeetingType, setSelectedMeetingType] = useState(DEFAULT_MEETING_TYPE);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editDate, setEditDate] = useState(null);
  const [editTime, setEditTime] = useState("");
  const [editDuration, setEditDuration] = useState(30);
  const [editLeadId, setEditLeadId] = useState("");
  const [editMeetingType, setEditMeetingType] = useState(DEFAULT_MEETING_TYPE);
  const [editMeetingFormat, setEditMeetingFormat] = useState("zoom");

  // Delete Confirmation State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingAppointment, setDeletingAppointment] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [appointmentsRes, leadsRes, slotsRes, unavailRes] = await Promise.all([
        axios.get(`${API}/admin/appointments`, { headers }),
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/public/available-slots`),
        axios.get(`${API}/scheduling/availability-config`).catch(() => ({ data: { weekly: [], specific_dates: [] } }))
      ]);
      setAppointments(appointmentsRes.data);
      setLeads(leadsRes.data);
      setAvailableSlots(slotsRes.data);
      setUnavailability(unavailRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  // Get contact name for a lead/client ID
  const getContactName = (leadId, clientId) => {
    const id = leadId || clientId;
    const lead = leads.find(l => l.id === id);
    if (lead) {
      const firstName = lead.first_name || "";
      const lastName = lead.last_name || "";
      if (firstName || lastName) {
        return `${firstName} ${lastName}`.trim();
      }
      return lead.contact_name || lead.email || "Unknown";
    }
    return "Unknown";
  };

  // Get company/business name for a lead/client ID
  const getCompanyName = (leadId, clientId) => {
    const id = leadId || clientId;
    const lead = leads.find(l => l.id === id);
    if (lead) {
      return lead.business_name || "—";
    }
    return "—";
  };

  // Calendar availability logic
  const availableDates = [...new Set(availableSlots.map(slot => slot.date))];

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;

    const dayOfWeek = date.getDay();
    if (unavailability.weekly && unavailability.weekly.includes(dayOfWeek)) return true;

    if (unavailability.specific_dates && unavailability.specific_dates.length > 0) {
      const dateStr = format(date, "yyyy-MM-dd");
      for (const blockout of unavailability.specific_dates) {
        const startDate = new Date(blockout.start_date);
        const endDate = new Date(blockout.end_date);
        const checkDate = new Date(dateStr);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        checkDate.setHours(0, 0, 0, 0);
        if (checkDate >= startDate && checkDate <= endDate) return true;
      }
    }
    return false;
  };

  const isDateAvailable = (date) => {
    if (isDateDisabled(date)) return false;
    const dateStr = format(date, "yyyy-MM-dd");
    return availableDates.includes(dateStr);
  };

  const getTimesForDate = (date) => {
    if (!date) return [];
    const dateStr = format(date, "yyyy-MM-dd");
    return availableSlots.filter(slot => slot.date === dateStr);
  };

  // Create Appointment
  const handleCreateAppointment = async () => {
    if (!selectedLeadId || !selectedDate || !selectedTime) {
      toast.error("Please select a contact, date, and time");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/admin/appointments`, {
        lead_id: selectedLeadId,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime,
        duration: selectedDuration,
        meeting_type: selectedMeetingType
      }, { headers });

      toast.success("Appointment created successfully");
      setSelectedLeadId("");
      setSelectedDate(null);
      setSelectedTime("");
      setSelectedDuration(30);
      setSelectedMeetingType("zoom");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create appointment");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Edit Modal
  const openEditModal = (appointment) => {
    setEditingAppointment(appointment);
    setEditDate(new Date(appointment.date));
    setEditTime(appointment.time);
    setEditDuration(appointment.duration || 30);
    setEditLeadId(appointment.lead_id || appointment.client_id || "");
    setEditMeetingType(appointment.meeting_type || "courtesy");
    setEditMeetingFormat(appointment.meeting_format || "zoom");
    setEditModalOpen(true);
  };

  // Save Edit
  const handleSaveEdit = async () => {
    if (!editDate || !editTime) {
      toast.error("Please select a date and time");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.put(`${API}/admin/appointments/${editingAppointment.id}`, {
        lead_id: editLeadId || null,
        client_id: null,
        date: format(editDate, "yyyy-MM-dd"),
        time: editTime,
        duration: editDuration,
        meeting_type: editMeetingType,
        meeting_format: editMeetingFormat
      }, { headers });

      toast.success("Appointment updated successfully");
      setEditModalOpen(false);
      setEditingAppointment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update appointment");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update Status
  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await axios.put(`${API}/admin/appointments/${appointmentId}/status?status=${newStatus}`, {}, { headers });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Delete Appointment
  const openDeleteModal = (appointment) => {
    setDeletingAppointment(appointment);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingAppointment) return;

    setIsSubmitting(true);
    try {
      await axios.delete(`${API}/admin/appointments/${deletingAppointment.id}`, { headers });
      toast.success("Appointment deleted");
      setDeleteModalOpen(false);
      setDeletingAppointment(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete appointment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      completed: "bg-green-500/20 text-green-400 border-green-500/30",
      canceled: "bg-red-500/20 text-red-400 border-red-500/30",
      no_show: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
    };
    const icons = {
      scheduled: <Clock className="w-3 h-3" />,
      completed: <CheckCircle className="w-3 h-3" />,
      canceled: <XCircle className="w-3 h-3" />,
      no_show: <AlertCircle className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.scheduled}`}>
        {icons[status]}
        {status?.replace("_", " ").toUpperCase()}
      </span>
    );
  };

  const getMeetingTypeBadge = (meetingType) => {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getMeetingTypeColor(meetingType)}`}>
        {getMeetingTypeLabel(meetingType)}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const timesForSelectedDate = getTimesForDate(selectedDate);
  const timesForEditDate = getTimesForDate(editDate);

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Header */}
      <header className="sticky top-0 z-50 bg-navy-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-slate-400 hover:text-white transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-white">
              Appointment Management
            </h1>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <CalendarIcon className="w-4 h-4" />
            {appointments.length} total appointments
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Create Appointment Section */}
        <div className="glass p-6 rounded-xl mb-8" data-testid="create-appointment-section">
          <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            Create New Appointment
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Contact Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300">Contact Name</Label>
              <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
                <SelectTrigger className="bg-navy-900 border-slate-700 text-white" data-testid="contact-select">
                  <SelectValue placeholder="Select a contact..." />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700">
                  {leads.map((lead) => {
                    const name = lead.first_name && lead.last_name
                      ? `${lead.first_name} ${lead.last_name}`
                      : lead.contact_name || lead.email;
                    return (
                      <SelectItem key={lead.id} value={lead.id} className="text-white hover:bg-navy-800">
                        {name} ({lead.business_name})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Calendar */}
            <div className="space-y-2">
              <Label className="text-slate-300">Date</Label>
              <div className="bg-navy-900 rounded-lg p-2 border border-slate-700">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => !isDateAvailable(date)}
                  className="rounded-md w-full"
                  classNames={{
                    months: "flex flex-col space-y-2",
                    month: "space-y-2",
                    caption: "flex justify-center pt-1 relative items-center mb-2",
                    caption_label: "text-sm font-medium text-white",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-6 w-6 bg-navy-800 hover:bg-primary/20 rounded flex items-center justify-center text-white border border-slate-700",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse",
                    head_row: "flex w-full justify-between mb-1",
                    head_cell: "text-slate-400 font-medium text-xs w-8 text-center",
                    row: "flex w-full justify-between",
                    cell: "relative p-0 text-center h-8 w-8",
                    day: "h-8 w-8 p-0 text-white hover:bg-primary/20 rounded flex items-center justify-center text-xs",
                    day_selected: "bg-primary text-white hover:bg-primary",
                    day_today: "bg-navy-800 text-white border border-slate-600",
                    day_disabled: "text-slate-700 opacity-30 cursor-not-allowed hover:bg-transparent",
                  }}
                />
              </div>
            </div>

            {/* Time Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300">Time (Mountain Time)</Label>
              {selectedDate ? (
                <div className="grid grid-cols-3 gap-1 max-h-64 overflow-y-auto bg-navy-900 rounded-lg p-2 border border-slate-700">
                  {timesForSelectedDate.map((slot, index) => (
                    <Button
                      key={index}
                      variant={selectedTime === slot.time ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedTime(slot.time)}
                      className={`text-xs ${selectedTime === slot.time ? "bg-primary text-white" : "border-slate-700 text-slate-300 hover:text-white"}`}
                    >
                      {formatTime12Hour(slot.time)}
                    </Button>
                  ))}
                  {timesForSelectedDate.length === 0 && (
                    <p className="col-span-3 text-slate-500 text-sm text-center py-4">No times available</p>
                  )}
                </div>
              ) : (
                <div className="bg-navy-900 rounded-lg p-4 border border-slate-700 text-center">
                  <p className="text-slate-500 text-sm">Select a date first</p>
                </div>
              )}
            </div>

            {/* Duration & Meeting Type & Submit */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Duration</Label>
                <Select value={String(selectedDuration)} onValueChange={(v) => setSelectedDuration(Number(v))}>
                  <SelectTrigger className="bg-navy-900 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-900 border-slate-700">
                    <SelectItem value="30" className="text-white hover:bg-navy-800">30 minutes</SelectItem>
                    <SelectItem value="60" className="text-white hover:bg-navy-800">60 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Meeting Type</Label>
                <Select value={selectedMeetingType} onValueChange={setSelectedMeetingType}>
                  <SelectTrigger className="bg-navy-900 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-900 border-slate-700">
                    {Object.entries(MEETING_TYPES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key} className="text-white hover:bg-navy-800">
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleCreateAppointment}
                disabled={!selectedLeadId || !selectedDate || !selectedTime || isSubmitting}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="create-appointment-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create Appointment
              </Button>
            </div>
          </div>
        </div>

        {/* Appointments Table */}
        <div className="glass rounded-xl overflow-hidden" data-testid="appointments-table">
          <div className="p-4 border-b border-slate-800">
            <h2 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              All Appointments
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-navy-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {appointments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                      No appointments scheduled yet
                    </td>
                  </tr>
                ) : (
                  appointments.map((apt) => (
                    <tr 
                      key={apt.id} 
                      className="hover:bg-navy-900/30 cursor-pointer transition-colors"
                      onClick={() => openEditModal(apt)}
                      data-testid={`appointment-row-${apt.id}`}
                    >
                      <td className="px-4 py-3 text-white font-medium">
                        {getContactName(apt.lead_id, apt.client_id)}
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {getCompanyName(apt.lead_id, apt.client_id)}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {formatShortDateMST(apt.date)}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {formatTime12Hour(apt.time)}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {apt.duration || 30} min
                      </td>
                      <td className="px-4 py-3">
                        {getMeetingTypeBadge(apt.meeting_type)}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(apt.status)}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <Select
                            value={apt.status}
                            onValueChange={(status) => handleStatusChange(apt.id, status)}
                          >
                            <SelectTrigger className="w-28 h-8 bg-navy-900 border-slate-700 text-xs text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-navy-900 border-slate-700">
                              <SelectItem value="scheduled" className="text-white hover:bg-navy-800 text-xs">Scheduled</SelectItem>
                              <SelectItem value="completed" className="text-white hover:bg-navy-800 text-xs">Completed</SelectItem>
                              <SelectItem value="canceled" className="text-white hover:bg-navy-800 text-xs">Canceled</SelectItem>
                              <SelectItem value="no_show" className="text-white hover:bg-navy-800 text-xs">No Show</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(apt)}
                            className="text-slate-400 hover:text-white"
                            data-testid={`edit-appointment-${apt.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteModal(apt)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-appointment-${apt.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Edit Appointment Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl bg-navy-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Edit Appointment
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Change the date, time, or meeting details
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            {/* Contact */}
            <div className="space-y-2">
              <Label className="text-slate-300">Contact</Label>
              <Select value={editLeadId} onValueChange={setEditLeadId}>
                <SelectTrigger className="bg-navy-950 border-slate-700 text-white">
                  <SelectValue placeholder="Select a contact..." />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700">
                  {leads.map((lead) => {
                    const name = lead.first_name && lead.last_name
                      ? `${lead.first_name} ${lead.last_name}`
                      : lead.contact_name || lead.email;
                    return (
                      <SelectItem key={lead.id} value={lead.id} className="text-white hover:bg-navy-800">
                        {name}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label className="text-slate-300">Duration</Label>
              <Select value={String(editDuration)} onValueChange={(v) => setEditDuration(Number(v))}>
                <SelectTrigger className="bg-navy-950 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700">
                  <SelectItem value="30" className="text-white hover:bg-navy-800">30 minutes</SelectItem>
                  <SelectItem value="60" className="text-white hover:bg-navy-800">60 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Meeting Format (Zoom/In-Person) */}
            <div className="space-y-2">
              <Label className="text-slate-300">Meeting Format</Label>
              <Select value={editMeetingFormat} onValueChange={setEditMeetingFormat}>
                <SelectTrigger className="bg-navy-950 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700">
                  <SelectItem value="zoom" className="text-white hover:bg-navy-800">
                    <span className="flex items-center gap-2">
                      <Video className="w-4 h-4" /> Zoom
                    </span>
                  </SelectItem>
                  <SelectItem value="in_person" className="text-white hover:bg-navy-800">
                    <span className="flex items-center gap-2">
                      <Building className="w-4 h-4" /> In-Person
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Meeting Type */}
            <div className="space-y-2">
              <Label className="text-slate-300">Meeting Type</Label>
              <Select value={editMeetingType} onValueChange={setEditMeetingType}>
                <SelectTrigger className="bg-navy-950 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700">
                  {Object.entries(MEETING_TYPES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key} className="text-white hover:bg-navy-800">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Calendar */}
            <div className="space-y-2">
              <Label className="text-slate-300">Date</Label>
              <div className="bg-navy-950 rounded-lg p-2 border border-slate-700">
                <Calendar
                  mode="single"
                  selected={editDate}
                  onSelect={setEditDate}
                  disabled={(date) => !isDateAvailable(date)}
                  className="rounded-md w-full"
                  classNames={{
                    months: "flex flex-col space-y-2",
                    month: "space-y-2",
                    caption: "flex justify-center pt-1 relative items-center mb-2",
                    caption_label: "text-sm font-medium text-white",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-6 w-6 bg-navy-800 hover:bg-primary/20 rounded flex items-center justify-center text-white border border-slate-700",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse",
                    head_row: "flex w-full justify-between mb-1",
                    head_cell: "text-slate-400 font-medium text-xs w-8 text-center",
                    row: "flex w-full justify-between",
                    cell: "relative p-0 text-center h-8 w-8",
                    day: "h-8 w-8 p-0 text-white hover:bg-primary/20 rounded flex items-center justify-center text-xs",
                    day_selected: "bg-primary text-white hover:bg-primary",
                    day_today: "bg-navy-800 text-white border border-slate-600",
                    day_disabled: "text-slate-700 opacity-30 cursor-not-allowed hover:bg-transparent",
                  }}
                />
              </div>
            </div>

            {/* Time Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300">Time (Mountain Time)</Label>
              {editDate ? (
                <div className="grid grid-cols-3 gap-1 max-h-48 overflow-y-auto bg-navy-950 rounded-lg p-2 border border-slate-700">
                  {timesForEditDate.map((slot, index) => (
                    <Button
                      key={index}
                      variant={editTime === slot.time ? "default" : "outline"}
                      size="sm"
                      onClick={() => setEditTime(slot.time)}
                      className={`text-xs ${editTime === slot.time ? "bg-primary text-white" : "border-slate-700 text-slate-300 hover:text-white"}`}
                    >
                      {formatTime12Hour(slot.time)}
                    </Button>
                  ))}
                  {timesForEditDate.length === 0 && (
                    <p className="col-span-3 text-slate-500 text-sm text-center py-4">No times available</p>
                  )}
                </div>
              ) : (
                <div className="bg-navy-950 rounded-lg p-4 border border-slate-700 text-center">
                  <p className="text-slate-500 text-sm">Select a date first</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="border-slate-700 text-slate-300">
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={!editDate || !editTime || isSubmitting}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="max-w-md bg-navy-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Delete Appointment
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete this appointment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deletingAppointment && (
            <div className="bg-navy-950 rounded-lg p-4 border border-slate-700 my-4">
              <p className="text-white font-medium">{getContactName(deletingAppointment.lead_id, deletingAppointment.client_id)}</p>
              <p className="text-slate-400 text-sm">
                {formatShortDateMST(deletingAppointment.date)} at {formatTime12Hour(deletingAppointment.time)}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)} className="border-slate-700 text-slate-300">
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-btn"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Appointment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAppointments;
